// Shared schedule resolver used by the public booking widget AND mirrored on
// the server (supabase/functions/book-appointment). Pure functions, no React.
//
// Inputs come straight from the admin tables:
//   clinic_schedule       — weekly hours + global booking rules
//   schedule_exceptions   — per-date closures / overrides
//   service_overrides     — per-service tweaks (duration, allowed days/windows,
//                           per-service buffer/step/lead)
//
// Everything is computed in clinic local time (Asia/Jerusalem by default). The
// caller is responsible for mapping wall-clock → UTC when needed.

export type Interval = { start: string; end: string };

export type WeeklyDay = {
  enabled: boolean;
  intervals: Interval[];
  breaks: Interval[];
};

export type ClinicSchedule = {
  weekly: WeeklyDay[];
  buffer_minutes: number;
  slot_step_minutes: number;
  min_lead_minutes: number;
  horizon_days: number;
  timezone: string;
};

export type ScheduleException = {
  date: string; // YYYY-MM-DD
  closed: boolean;
  intervals: Interval[];
  note?: string | null;
};

export type ServiceWindowKind = 'working' | 'break' | 'other';
export type ServiceWindow = {
  weekday: number; // 0..6
  kind: ServiceWindowKind;
  start: string;
  end: string;
  note?: string;
};

export type ServiceOverride = {
  shopify_product_id: string;
  bookable: boolean;
  duration_minutes: number;
  color?: string | null;
  allowed_weekdays?: number[] | null;
  allowed_windows?: ServiceWindow[] | null;
  allowed_dates?: string[] | null;
  max_per_day?: number | null;
  buffer_minutes?: number | null;
  slot_step_minutes?: number | null;
  min_lead_minutes?: number | null;
};

export type BusyBlock = { start: number; end: number }; // UTC ms

// ---------- time helpers ----------

export const toMin = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

export const fromMin = (m: number): string =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Asia/Jerusalem wall clock <-> UTC ms (handles DST).
export function tzOffsetMin(utcMs: number, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
    }).formatToParts(new Date(utcMs));
    const off = parts.find((p) => p.type === 'timeZoneName')?.value;
    const m = off?.match(/GMT([+-])(\d+)(?::?(\d+))?/);
    if (!m) return 180;
    const sign = m[1] === '+' ? 1 : -1;
    return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
  } catch {
    return 180;
  }
}

export function wallToUtcMs(
  y: number,
  mo: number, // 0-based
  d: number,
  hh: number,
  mm: number,
  timeZone = 'Asia/Jerusalem',
): number {
  const guess = Date.UTC(y, mo, d, hh, mm);
  const off = tzOffsetMin(guess, timeZone);
  return guess - off * 60_000;
}

// ---------- interval math ----------

// Subtract a list of intervals (in minutes) from a base list. Returns the
// remaining "free" intervals. Used to remove breaks from working windows.
function subtractIntervals(
  base: Array<[number, number]>,
  subs: Array<[number, number]>,
): Array<[number, number]> {
  let out = base.map((iv) => [...iv] as [number, number]);
  for (const [ss, se] of subs) {
    const next: Array<[number, number]> = [];
    for (const [s, e] of out) {
      if (se <= s || ss >= e) {
        next.push([s, e]);
        continue;
      }
      if (ss > s) next.push([s, Math.min(ss, e)]);
      if (se < e) next.push([Math.max(se, s), e]);
    }
    out = next.filter(([s, e]) => e > s);
  }
  return out;
}

function mergeIntervals(list: Array<[number, number]>): Array<[number, number]> {
  if (list.length === 0) return list;
  const sorted = [...list].sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = out[out.length - 1];
    const cur = sorted[i];
    if (cur[0] <= last[1]) last[1] = Math.max(last[1], cur[1]);
    else out.push(cur);
  }
  return out;
}

// ---------- daily window resolution ----------

// Returns the bookable [start,end) intervals (in minutes from midnight) for a
// given date, after applying weekly schedule, exceptions, and the union of
// selected services' allowed weekdays/windows.
export function resolveDayIntervals(
  date: Date,
  schedule: ClinicSchedule,
  exceptions: ScheduleException[],
  services: ServiceOverride[],
): Array<[number, number]> {
  // 1. Per-service hard filters: bookable + allowed_weekdays + allowed_dates.
  const weekday = date.getDay();
  const dateStr = ymd(date);
  for (const svc of services) {
    if (!svc.bookable) return [];
    if (svc.allowed_weekdays && svc.allowed_weekdays.length > 0
        && !svc.allowed_weekdays.includes(weekday)) return [];
    if (svc.allowed_dates && svc.allowed_dates.length > 0
        && !svc.allowed_dates.includes(dateStr)) return [];
  }

  // 2. Start from weekly day.
  const baseDay = schedule.weekly[weekday];
  if (!baseDay || !baseDay.enabled) {
    // Exceptions can still open a closed day, but only if intervals are set
    // and closed=false.
  }

  let working: Array<[number, number]> = [];
  let breaks: Array<[number, number]> = [];

  if (baseDay?.enabled) {
    working = baseDay.intervals.map((iv) => [toMin(iv.start), toMin(iv.end)] as [number, number]);
    breaks = baseDay.breaks.map((iv) => [toMin(iv.start), toMin(iv.end)] as [number, number]);
  }

  // 3. Apply date exception.
  const exc = exceptions.find((e) => e.date === dateStr);
  if (exc) {
    if (exc.closed) return [];
    if (exc.intervals && exc.intervals.length > 0) {
      working = exc.intervals.map((iv) => [toMin(iv.start), toMin(iv.end)] as [number, number]);
      breaks = [];
    }
  }

  if (working.length === 0) return [];

  // 4. Apply per-service windows for this weekday: "working" extends
  // available time, "break"/"other" subtracts from it.
  for (const svc of services) {
    const wins = (svc.allowed_windows ?? []).filter((w) => w.weekday === weekday);
    if (wins.length === 0) continue;
    const adds = wins.filter((w) => w.kind === 'working');
    const subs = wins.filter((w) => w.kind !== 'working');
    if (adds.length > 0) {
      // If a service explicitly lists working windows for this weekday,
      // intersect base with them so the service can only be booked in its
      // own allowed time. This is the most predictable behavior for an
      // admin who set, e.g., "thai-scalp-massage: only ה׳ 16:00–18:00".
      const allow = adds.map((w) => [toMin(w.start), toMin(w.end)] as [number, number]);
      const intersected: Array<[number, number]> = [];
      for (const [bs, be] of working) {
        for (const [as, ae] of allow) {
          const s = Math.max(bs, as);
          const e = Math.min(be, ae);
          if (e > s) intersected.push([s, e]);
        }
      }
      working = intersected;
    }
    for (const w of subs) {
      breaks.push([toMin(w.start), toMin(w.end)]);
    }
  }

  if (working.length === 0) return [];

  // 5. Subtract breaks.
  const free = subtractIntervals(mergeIntervals(working), mergeIntervals(breaks));
  return free.filter(([s, e]) => e > s);
}

// ---------- effective booking rules ----------

export type EffectiveRules = {
  buffer_minutes: number;
  slot_step_minutes: number;
  min_lead_minutes: number;
  total_duration: number;
};

export function effectiveRules(
  schedule: ClinicSchedule,
  services: ServiceOverride[],
): EffectiveRules {
  const pick = (key: 'buffer_minutes' | 'slot_step_minutes' | 'min_lead_minutes') => {
    const values = services
      .map((s) => s[key])
      .filter((v): v is number => typeof v === 'number');
    if (values.length === 0) return schedule[key];
    // Stricter wins: max buffer, max lead, max step (coarser grid).
    return Math.max(...values);
  };
  return {
    buffer_minutes: pick('buffer_minutes'),
    slot_step_minutes: pick('slot_step_minutes'),
    min_lead_minutes: pick('min_lead_minutes'),
    total_duration: services.reduce((sum, s) => sum + s.duration_minutes, 0),
  };
}

// ---------- slot generation ----------

export function slotConflictsBusy(
  startUtcMs: number,
  durationMin: number,
  bufferMin: number,
  busy: BusyBlock[],
): boolean {
  const s = startUtcMs - bufferMin * 60_000;
  const e = startUtcMs + (durationMin + bufferMin) * 60_000;
  return busy.some((b) => b.start < e && b.end > s);
}

export function generateSlotsForDay(
  date: Date,
  schedule: ClinicSchedule,
  exceptions: ScheduleException[],
  services: ServiceOverride[],
  busy: BusyBlock[],
  nowMs: number,
): string[] {
  const rules = effectiveRules(schedule, services);
  if (rules.total_duration <= 0) return [];

  // Horizon
  const horizonMs = nowMs + schedule.horizon_days * 86_400_000;
  const dayStartMs = wallToUtcMs(
    date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, schedule.timezone,
  );
  if (dayStartMs > horizonMs) return [];

  const earliestMs = nowMs + rules.min_lead_minutes * 60_000;
  const intervals = resolveDayIntervals(date, schedule, exceptions, services);

  const out: string[] = [];
  for (const [startMin, endMin] of intervals) {
    if (endMin - startMin < rules.total_duration) continue;
    for (let t = startMin; t + rules.total_duration <= endMin; t += rules.slot_step_minutes) {
      const hh = Math.floor(t / 60);
      const mm = t % 60;
      const utcMs = wallToUtcMs(
        date.getFullYear(), date.getMonth(), date.getDate(), hh, mm, schedule.timezone,
      );
      if (utcMs < earliestMs) continue;
      if (slotConflictsBusy(utcMs, rules.total_duration, rules.buffer_minutes, busy)) continue;
      out.push(fromMin(t));
    }
  }
  return out;
}

export function isDayBookable(
  date: Date,
  schedule: ClinicSchedule,
  exceptions: ScheduleException[],
  services: ServiceOverride[],
  busy: BusyBlock[],
  nowMs: number,
): boolean {
  return generateSlotsForDay(date, schedule, exceptions, services, busy, nowMs).length > 0;
}

// Server-side: validate that a given UTC start fits all rules. Returns null on
// success or a short error code.
export function validateBooking(
  startUtcMs: number,
  schedule: ClinicSchedule,
  exceptions: ScheduleException[],
  services: ServiceOverride[],
  busy: BusyBlock[],
  nowMs: number,
): null | 'past' | 'lead_time' | 'horizon' | 'closed_day' | 'outside_window' | 'busy' | 'not_bookable' {
  const rules = effectiveRules(schedule, services);
  if (services.some((s) => !s.bookable)) return 'not_bookable';
  if (rules.total_duration <= 0) return 'not_bookable';
  if (startUtcMs < nowMs) return 'past';
  if (startUtcMs < nowMs + rules.min_lead_minutes * 60_000) return 'lead_time';
  if (startUtcMs > nowMs + schedule.horizon_days * 86_400_000) return 'horizon';

  // Convert UTC start to clinic wall-clock to find date + minute-of-day.
  const offMin = tzOffsetMin(startUtcMs, schedule.timezone);
  const local = new Date(startUtcMs + offMin * 60_000);
  const date = new Date(
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()),
  );
  // Build a "virtual local Date" by using UTC fields as local-clinic fields.
  const fakeLocal = new Date(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
  const startMin = local.getUTCHours() * 60 + local.getUTCMinutes();
  const endMin = startMin + rules.total_duration;

  const intervals = resolveDayIntervals(fakeLocal, schedule, exceptions, services);
  if (intervals.length === 0) return 'closed_day';
  const fits = intervals.some(([s, e]) => s <= startMin && endMin <= e);
  if (!fits) return 'outside_window';

  if (slotConflictsBusy(startUtcMs, rules.total_duration, rules.buffer_minutes, busy)) return 'busy';
  return null;
}
